import { useState, useEffect, useRef } from "react";
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Volume1, 
  Search, 
  Radio as RadioIcon, 
  Music,
  Flame,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface RadioStation {
  id: string;
  name: string;
  streamUrl: string;
  category: "slovenian" | "balkan";
  color: string; // Tailwind glow color class
  bgGradient: string; // Gradient color for cards
  subtitle: string;
  logoText: string;
}

const STATIONS: RadioStation[] = [
  {
    id: "enter-radio",
    name: "Enter Radio",
    streamUrl: "https://stream.nextmedia.si/proxy/enter_1?mp=/enter?uid=ntrweb",
    category: "balkan",
    color: "rgba(244, 63, 94, 0.4)", // Rose glow
    bgGradient: "from-rose-600/20 to-rose-900/30 border-rose-500/20",
    subtitle: "Premium electronic, dance, EDM and hot balkan club hits",
    logoText: "ENTR",
  },
  {
    id: "val-202",
    name: "Val 202",
    streamUrl: "https://mp3.rtvslo.si/val202",
    category: "slovenian",
    color: "rgba(59, 130, 246, 0.4)", // Blue glow
    bgGradient: "from-blue-600/20 to-blue-900/30 border-blue-500/20",
    subtitle: "Lively pop, rock, indie, youth program and news",
    logoText: "202",
  },
  {
    id: "radio-si",
    name: "Radio SI",
    streamUrl: "https://mp3.rtvslo.si/rsi",
    category: "slovenian",
    color: "rgba(16, 185, 129, 0.4)", // Emerald glow
    bgGradient: "from-emerald-600/20 to-emerald-900/30 border-emerald-500/20",
    subtitle: "Slovenia International - Pop, Dance, hits in English & Slovenian",
    logoText: "R-SI",
  },
  {
    id: "radio-prvi",
    name: "Radio Prvi",
    streamUrl: "https://mp3.rtvslo.si/prvi",
    category: "slovenian",
    color: "rgba(239, 68, 68, 0.4)", // Red glow
    bgGradient: "from-red-600/20 to-red-900/30 border-red-500/20",
    subtitle: "Slovenian pop, traditional hits, talk show and news",
    logoText: "PRVI",
  },
  {
    id: "radio-maribor",
    name: "Radio Maribor",
    streamUrl: "https://mp3.rtvslo.si/rmb",
    category: "slovenian",
    color: "rgba(245, 158, 11, 0.4)", // Amber glow
    bgGradient: "from-amber-600/20 to-amber-900/30 border-amber-500/20",
    subtitle: "Popular local pop hits and regional news from Maribor",
    logoText: "R-MB",
  },
  {
    id: "radio-koper",
    name: "Radio Koper",
    streamUrl: "https://mp3.rtvslo.si/rakp",
    category: "slovenian",
    color: "rgba(6, 182, 212, 0.4)", // Cyan glow
    bgGradient: "from-cyan-600/20 to-cyan-900/30 border-cyan-500/20",
    subtitle: "Regional pop hits and local news from Primorska",
    logoText: "R-KP",
  },
  {
    id: "radio-capodistria",
    name: "Radio Capodistria",
    streamUrl: "https://mp3.rtvslo.si/capo",
    category: "balkan",
    color: "rgba(236, 72, 153, 0.4)", // Pink glow
    bgGradient: "from-pink-600/20 to-pink-900/30 border-pink-500/20",
    subtitle: "Premium Italian & Ex-Yu pop hits, news and culture",
    logoText: "CAPO",
  },
  {
    id: "radio-ars",
    name: "Radio Ars",
    streamUrl: "https://mp3.rtvslo.si/ars",
    category: "slovenian",
    color: "rgba(168, 85, 247, 0.4)", // Purple glow
    bgGradient: "from-purple-600/20 to-purple-900/30 border-purple-500/20",
    subtitle: "Relaxing classical music, opera, jazz and cultural programs",
    logoText: "ARS",
  }
];

export function Radio() {
  const [currentStation, setCurrentStation] = useState<RadioStation | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<"all" | "slovenian" | "balkan">("all");
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Sync volume with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Handle playing/pausing stations
  const playStation = (station: RadioStation) => {
    if (!audioRef.current) return;
    setErrorText(null);

    const isCurrent = currentStation?.id === station.id;

    if (isCurrent) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setLoading(true);
        audioRef.current.play().catch(err => {
          console.error("Playback start error:", err);
          setErrorText("Could not start audio stream. Browser autoplay or network blocked.");
          setIsPlaying(false);
          setLoading(false);
        });
      }
    } else {
      setLoading(true);
      setCurrentStation(station);
      setIsPlaying(false);

      // Directly assign src to bypass state delay
      audioRef.current.src = station.streamUrl;
      audioRef.current.load();
      
      // Auto-play the new station stream
      audioRef.current.play().catch(err => {
        console.error("Playback start error on new station:", err);
        setErrorText(`Insecure or blocked stream. Try another station like Val 202 or Radio SI.`);
        setIsPlaying(false);
        setLoading(false);
      });
    }
  };

  const toggleGlobalPlay = () => {
    if (!audioRef.current) return;
    setErrorText(null);

    if (!currentStation) {
      const available = filteredStations;
      if (available.length > 0) {
        playStation(available[0]);
      }
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setLoading(true);
      audioRef.current.play().catch(err => {
        console.error("Global play failed:", err);
        setErrorText("Playback blocked by browser security policy. Please click a station directly.");
        setIsPlaying(false);
        setLoading(false);
      });
    }
  };

  const filteredStations = STATIONS.filter((s) => {
    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          s.subtitle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "all" || s.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8 max-w-5xl mx-auto px-4 py-4 md:py-8">
      {/* HTML5 Audio Element in DOM (100% Reliable Lifecycle) */}
      <audio
        ref={audioRef}
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onPlaying={() => {
          setIsPlaying(true);
          setLoading(false);
        }}
        onLoadStart={() => setLoading(true)}
        onWaiting={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
        onError={(e) => {
          console.error("Native audio element error event:", e);
          setErrorText("Failed to load stream (SSL/CORS mismatch or offline stream).");
          setIsPlaying(false);
          setLoading(false);
        }}
      />

      {/* Dynamic Soundwave Animations Styles */}
      <style>{`
        @keyframes soundwave {
          0%, 100% { height: 8px; }
          50% { height: 40px; }
        }
        .sound-bar {
          animation: soundwave 1.2s ease-in-out infinite;
        }
        .sound-bar:nth-child(1) { animation-delay: 0.1s; }
        .sound-bar:nth-child(2) { animation-delay: 0.4s; }
        .sound-bar:nth-child(3) { animation-delay: 0.2s; }
        .sound-bar:nth-child(4) { animation-delay: 0.6s; }
        .sound-bar:nth-child(5) { animation-delay: 0.3s; }
      `}</style>

      {/* Header — hero band */}
      <div className="relative overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-medium text-emerald-400 mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live streaming
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight">
            Areon <span className="text-primary">Radio</span>
          </h1>
          <p className="text-[13px] sm:text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">
            Listen to the best live radio stations from Slovenia and the Balkan region directly on our website — completely public and mobile-friendly.
          </p>
        </div>
      </div>

      {/* Error Alert if stream fails */}
      {errorText && (
        <div className="flex items-center gap-3 p-4 bg-destructive/15 border border-destructive/30 rounded-xl text-destructive-foreground text-sm animate-shake">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
          <span>{errorText}</span>
        </div>
      )}

      {/* Now-playing player */}
      <Card className="relative overflow-hidden border-border/50 bg-card/60 p-5 sm:p-6 flex flex-col md:flex-row items-center gap-5 md:gap-6">
        {currentStation && (
          <div
            className="absolute -left-16 -top-16 w-56 h-56 rounded-full blur-[90px] opacity-20 pointer-events-none transition-all duration-500"
            style={{ backgroundColor: currentStation.color }}
          />
        )}

        <div className="relative flex items-center gap-4 w-full md:w-auto flex-1 min-w-0">
          {/* Artwork */}
          <div
            className={`h-20 w-20 sm:h-24 sm:w-24 rounded-2xl flex items-center justify-center font-bold text-2xl shrink-0 select-none transition-all duration-500 bg-gradient-to-br ${
              currentStation ? "text-white" : "from-primary/20 to-primary/5 text-primary ring-1 ring-primary/20"
            }`}
            style={{
              backgroundImage: currentStation ? `linear-gradient(135deg, ${currentStation.color.replace("0.4", "0.7")}, #09090b)` : undefined,
            }}
          >
            {currentStation ? currentStation.logoText : <RadioIcon className="h-9 w-9" />}
          </div>

          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-400 mb-1">
              <span className={`h-1.5 w-1.5 rounded-full bg-emerald-400 ${isPlaying ? "animate-pulse" : "opacity-40"}`} />
              {currentStation ? (isPlaying ? (loading ? "Buffering…" : "Now playing") : "Paused") : "Select a station"}
            </div>
            <h2 className="text-lg sm:text-xl font-semibold truncate">
              {currentStation ? currentStation.name : "Listen to Radio"}
            </h2>
            <p className="text-muted-foreground text-xs sm:text-[13px] line-clamp-2 mt-0.5">
              {currentStation ? currentStation.subtitle : "Choose a radio station from the list below to play."}
            </p>
          </div>

          {/* Visualizer */}
          <div className="hidden sm:flex items-end gap-1 h-9 w-16 justify-center select-none shrink-0">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={isPlaying && !loading ? "sound-bar w-1 rounded-full bg-primary" : "w-1 rounded-full h-1.5 bg-muted"}
              />
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="relative flex items-center gap-4 w-full md:w-auto shrink-0 border-t md:border-t-0 pt-4 md:pt-0 border-border/40 justify-center">
          <Button
            size="icon"
            onClick={toggleGlobalPlay}
            disabled={loading}
            className="h-13 w-13 sm:h-14 sm:w-14 rounded-full shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground hover:scale-105 active:scale-95 transition-transform"
          >
            {loading ? (
              <div className="h-5 w-5 rounded-full border-[3px] border-current border-t-transparent animate-spin" />
            ) : isPlaying ? (
              <Pause className="h-6 w-6 fill-current" />
            ) : (
              <Play className="h-6 w-6 fill-current translate-x-0.5" />
            )}
          </Button>

          <div className="flex items-center gap-2 w-full sm:w-32 shrink-0">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsMuted(!isMuted)}
              className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : volume < 0.4 ? (
                <Volume1 className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                setVolume(Number(e.target.value));
                setIsMuted(false);
              }}
              className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-primary bg-muted outline-none"
            />
          </div>
        </div>
      </Card>

      {/* Toolbar: category filter + search */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-xl border border-border/50 bg-card/50 p-1 w-fit">
          {([
            { key: "all", label: "All" },
            { key: "slovenian", label: "Slovenian" },
            { key: "balkan", label: "Balkan" },
          ] as const).map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveCategory(c.key)}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                activeCategory === c.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search stations…"
            className="pl-10"
          />
        </div>
      </div>

      {/* Grid of Stations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredStations.map((station) => {
          const isActive = currentStation?.id === station.id;
          return (
            <Card
              key={station.id}
              onClick={() => playStation(station)}
              className={`group flex flex-col p-3 border bg-card/40 hover:bg-card/70 cursor-pointer transition-colors select-none ${
                isActive ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20" : "border-border/50"
              }`}
            >
              {/* Artwork */}
              <div className={`relative h-32 rounded-xl flex items-center justify-center font-bold text-3xl text-white/90 select-none bg-gradient-to-br ${station.bgGradient} overflow-hidden`}>
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center scale-90 group-hover:scale-100 transition-transform duration-200">
                    {isActive && isPlaying ? (
                      <Pause className="h-4 w-4 fill-current" />
                    ) : (
                      <Play className="h-4 w-4 fill-current translate-x-0.5" />
                    )}
                  </div>
                </div>
                <span>{station.logoText}</span>
                {isActive && (
                  <div className="absolute top-2 right-2 flex items-end gap-0.5 h-3.5">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className={isPlaying && !loading ? "sound-bar w-0.5 rounded-full bg-white" : "w-0.5 h-1 rounded-full bg-white/50"} />
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mt-3 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-semibold text-sm text-foreground truncate group-hover:text-primary transition-colors">
                    {station.name}
                  </h3>
                  <p className="text-muted-foreground text-xs mt-0.5 line-clamp-2 leading-relaxed">
                    {station.subtitle}
                  </p>
                </div>
                {isActive && (
                  <div className="mt-2.5 pt-2 border-t border-primary/10 flex items-center gap-1.5 text-[11px] text-primary font-medium">
                    <Music className="h-3 w-3" />
                    {isPlaying ? (loading ? "Loading…" : "Now playing") : "Paused"}
                  </div>
                )}
              </div>
            </Card>
          );
        })}

        {filteredStations.length === 0 && (
          <div className="col-span-full text-center py-12 space-y-2 border border-dashed border-border rounded-xl">
            <RadioIcon className="h-10 w-10 text-muted-foreground mx-auto animate-bounce" />
            <p className="text-muted-foreground text-sm font-semibold">No radio stations match your query.</p>
          </div>
        )}
      </div>
    </div>
  );
}
